import React, { ChangeEvent, ReactNode, useEffect, useRef } from 'react';
import styles from './FileInput.module.scss';
import cx from 'classnames';
import { useIntl } from 'react-intl';
import { translate } from '../../utility/messageTranslator/translate';
import {
  faFileCircleCheck,
  faFileCircleExclamation,
  faFileImport,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Asset } from '../../domain/Asset';
import { Clear } from '@mui/icons-material';
import { HttpError } from '../../config/Axios/axios-instance';

type Props = {
  name: string;
  multiple?: boolean;
  value: File | File[] | string | string[] | undefined;
  onFileChange?: (event: ChangeEvent<any>) => void;
  onImageDelete?: (name: string, value: string | File) => void;
  onSetValidationErrors?: (error: HttpError) => void;
  maxFileCount?: number;
  errors?: Array<string>;
  label?: string | ReactNode;
  helperText?: string;
  asset?: Asset | null;
  onlyPdf?: boolean;
};

const FileInput = ({
  onFileChange,
  value,
  name,
  maxFileCount,
  multiple,
  errors,
  label,
  helperText,
  asset,
  onImageDelete,
  onSetValidationErrors,
  onlyPdf,
}: Props) => {
  const intl = useIntl();

  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const handleUploadCLick = () => {
    if (uploadInputRef.current) {
      uploadInputRef.current.click();
    }
  };

  const getIcon = () => {
    if (errors && errors.length > 0) {
      return faFileCircleExclamation;
    }

    if (value && (!errors || errors.length === 0)) {
      return faFileCircleCheck;
    }

    return faFileImport;
  };

  const renderAddFileButton = () => {
    const hasValue = Array.isArray(value) ? !!value.length : !!value;
    const uploadedCount = Array.isArray(value) && value.length;

    return (
      <div className={styles.innerFileSelectContainer}>
        <FontAwesomeIcon
          className={cx(styles.innerFileIcon, {
            [styles.errorFile]: errors && errors.length > 0,
            [styles.successFile]: hasValue && (!errors || errors.length === 0),
          })}
          icon={getIcon()}
          fixedWidth
          size="2x"
        />
        <span className={styles.innerFileSelectLabel}>
          {hasValue &&
          (!errors || errors.length === 0) &&
          (!multiple ||
            (maxFileCount && uploadedCount === maxFileCount) ||
            onlyPdf)
            ? translate(intl, 'INPUTS.READY_TO_UPLOAD')
            : translate(
                intl,
                multiple
                  ? onlyPdf
                    ? 'INPUTS.UPLOAD_PDFS'
                    : 'INPUTS.UPLOAD_IMAGES'
                  : onlyPdf
                  ? 'INPUTS.UPLOAD_PDF'
                  : 'INPUTS.UPLOAD_IMAGE',
              )}
        </span>
      </div>
    );
  };

  const renderImage = (value: File | string, index: number) => {
    return (
      <div key={`image-${index}`} className={styles.imageContainer}>
        {multiple && (
          <div className={styles.delete}>
            <Clear
              color="inherit"
              onClick={() => {
                onImageDelete?.(name, value);

                if (uploadInputRef?.current) {
                  uploadInputRef.current.value = '';
                }
              }}
            />
          </div>
        )}
        {value instanceof File ? (
          <img src={URL.createObjectURL(value)} alt={value.name} />
        ) : (
          <img src={value.toString()} alt={value?.toString() || ''} />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (
      multiple &&
      maxFileCount &&
      Array.isArray(value) &&
      maxFileCount < value.length &&
      onSetValidationErrors
    ) {
      onSetValidationErrors([
        {
          field: name,
          message: translate(intl, 'VALIDATION.TOO_MANY_FILES'),
        },
      ]);
    }
  }, [value, multiple, maxFileCount]);

  return (
    <div className={styles.fileFieldContainer}>
      <div className={styles.fileField}>
        <input
          color="primary"
          type="file"
          onChange={onFileChange}
          ref={uploadInputRef}
          name={name}
          multiple={multiple}
          accept={onlyPdf ? '.pdf' : '.jpeg,.jpg,.png'}
        />
        <div className={styles.fileContainer}>
          <div
            className={styles.fileSelectedContainer}
            onClick={handleUploadCLick}
          >
            {value && !multiple && !onlyPdf
              ? renderImage(value as File | string, 0)
              : renderAddFileButton()}
          </div>
        </div>
        <div className={styles.label}>
          <h5>{label}</h5>
          <p>{translate(intl, 'INPUTS.MAX_FILE_SIZE_LABEL')}</p>
          <p>
            {translate(
              intl,
              onlyPdf
                ? 'INPUTS.ALLOWED_PDF_EXTENSIONS'
                : 'INPUTS.ALLOWED_FILE_EXTENSIONS_LABEL',
            )}
          </p>
          {multiple && maxFileCount && (
            <p>
              {translate(intl, 'INPUTS.MAX_INPUT_COUNT').replace(
                ':max',
                maxFileCount.toString(),
              )}
            </p>
          )}
          {!multiple && !Array.isArray(asset) && (
            <p className={styles.selectedFileLabel}>
              {value && value instanceof File && (
                <FontAwesomeIcon
                  icon={faFileImport as IconProp}
                  fixedWidth
                  size="sm"
                  className={styles.selectedFileIcon}
                />
              )}
              <span>
                {value && value instanceof File
                  ? value.name
                  : asset?.fileName ?? ''}
              </span>
            </p>
          )}
          {errors &&
            errors.map((error, index) => (
              <div className={styles.fileError} key={`file-error-${index}`}>
                {error}
              </div>
            ))}
          {helperText && (
            <button
              type="button"
              className={styles.helperText}
              onClick={handleUploadCLick}
            >
              {helperText}
            </button>
          )}
        </div>
      </div>
      {multiple && Array.isArray(value) && !!value.length && (
        <div className={styles.multipleImageContainer}>
          {Array.isArray(value) &&
            value.map((image, index) =>
              renderImage(image as File | string, index),
            )}
        </div>
      )}
    </div>
  );
};

export default FileInput;
